export const BRAND_ASSET_UPDATED_AT = "2026-08-14";
export const BRAND_WORDMARK = "rešimoprobleme";

export const BRAND_COLORS = {
  forest: "#103f39",
  teal: "#0d6c65",
  tealHover: "#0b655f",
  ink: "#17211f",
  paper: "#fbf8ec",
  quietPaper: "#f1e9d2",
  hairline: "#e5ded2",
  controlEdge: "#7f7669",
  white: "#ffffff",
  mutedForeground: "#625c50",
  // Export and illustration asset only. Runtime product surfaces use the two
  // cream semantic tokens, never this decorative teal tint.
  tealSoft: "#e8f2f0",
} as const;

export const BRAND_MARK = {
  lineViewBox: "104 102 298 319",
  iconStrokeWidth: 20,
  lockupStrokeWidth: 30,
  paths: [
    "M180.0 133.0C185.9 130.0 208.5 117.4 217.0 114.0C225.5 110.6 224.8 112.2 233.0 112.0C241.2 111.8 256.2 110.6 268.0 113.0C279.8 115.4 298.7 123.3 307.0 127.0C315.3 130.7 313.8 129.9 320.0 136.0C326.2 142.1 339.9 156.2 346.0 165.0C352.1 173.8 356.2 181.9 358.0 191.0C359.8 200.1 351.6 210.2 357.0 222.0C362.4 233.8 386.4 256.7 392.0 265.0C397.6 273.3 394.7 270.8 392.0 274.0C389.3 277.2 377.1 281.3 375.0 285.0C372.9 288.7 378.8 293.2 379.0 297.0C379.2 300.8 376.2 305.3 376.0 309.0C375.8 312.7 379.4 316.5 378.0 320.0C376.6 323.5 368.6 324.9 367.0 331.0C365.4 337.1 369.4 352.1 368.0 358.0C366.6 363.9 363.4 365.9 358.0 368.0C352.6 370.1 345.2 372.0 334.0 371.0C322.8 370.0 297.9 364.9 288.0 362.0C278.1 359.1 276.5 356.4 272.0 353.0C267.5 349.6 262.6 344.5 260.0 341.0C257.4 337.5 256.6 332.6 256.0 331.0",
    "M157.0 284.0C158.9 288.3 165.6 301.2 169.0 311.0C172.4 320.8 176.4 333.6 178.0 345.0C179.6 356.4 179.6 371.4 179.0 382.0C178.4 392.6 174.8 406.4 174.0 411.0",
    "M302.0 381.0C301.8 385.8 301.2 406.2 301.0 411.0",
    "M151.0 146.0C157.1 145.4 164.3 145.1 171.0 148.0C177.7 150.9 186.0 163.8 193.0 164.0C200.0 164.2 207.6 151.9 215.0 149.0C222.4 146.1 231.8 144.9 239.0 146.0C246.2 147.1 254.4 151.4 260.0 156.0C265.6 160.6 271.8 167.5 274.0 175.0C276.2 182.5 276.6 193.6 274.0 203.0C271.4 212.4 264.1 225.2 258.0 234.0C251.9 242.8 245.4 249.7 236.0 258.0C226.6 266.3 206.7 281.7 199.0 286.0C191.3 290.3 193.9 288.2 188.0 285.0C182.1 281.8 169.7 272.6 162.0 266.0C154.3 259.4 147.2 253.0 140.0 244.0C132.8 235.0 121.2 220.2 117.0 210.0C112.8 199.8 113.5 187.2 114.0 180.0C114.5 172.8 117.0 169.5 120.0 165.0C123.0 160.5 128.0 155.0 133.0 152.0C138.0 149.0 144.9 146.6 151.0 146.0Z",
  ],
} as const;

export type BrandAssetKind = "lockup" | "wordmark" | "mark" | "avatar";
export type BrandAssetSurface = "light" | "dark" | "any";

export type BrandAsset = {
  id: string;
  kind: BrandAssetKind;
  label: string;
  usage: string;
  surface: BrandAssetSurface;
  previewClassName: string;
  svg: string;
  png: string;
  jpeg: string;
};

const assetPath = (name: string, extension: "svg" | "png" | "jpg") =>
  `/brand/${name}.${extension}`;

type BrandAssetDefinition = Omit<BrandAsset, "svg" | "png" | "jpeg"> & {
  fileName: string;
};

const asset = <const Entry extends BrandAssetDefinition>(input: Entry) => {
  const { fileName, ...entry } = input;

  return {
    ...entry,
    svg: assetPath(fileName, "svg"),
    png: assetPath(fileName, "png"),
    jpeg: assetPath(fileName, "jpg"),
  } as Omit<Entry, "fileName"> & Pick<BrandAsset, "svg" | "png" | "jpeg">;
};

export const BRAND_ASSETS = [
  asset({
    id: "lockup-color",
    fileName: "resimo-probleme-lockup-color",
    kind: "lockup",
    label: "Logotip u boji",
    usage: "Podrazumevani logotip za svetle površine.",
    surface: "light",
    previewClassName: "bg-background",
  }),
  asset({
    id: "lockup-forest",
    fileName: "resimo-probleme-lockup-forest",
    kind: "lockup",
    label: "Jednobojni logotip",
    usage: "Štampa i primene koje zahtevaju jednu boju.",
    surface: "light",
    previewClassName: "bg-background",
  }),
  asset({
    id: "lockup-white",
    fileName: "resimo-probleme-lockup-white",
    kind: "lockup",
    label: "Beli logotip",
    usage: "Tamne šumsko zelene ili mastilo površine.",
    surface: "dark",
    previewClassName: "bg-brand-forest",
  }),
  asset({
    id: "wordmark-color",
    fileName: "resimo-probleme-wordmark-color",
    kind: "wordmark",
    label: "Naziv u boji",
    usage: "Kada je znak već prisutan u neposrednom kontekstu.",
    surface: "light",
    previewClassName: "bg-background",
  }),
  asset({
    id: "wordmark-forest",
    fileName: "resimo-probleme-wordmark-forest",
    kind: "wordmark",
    label: "Jednobojni naziv",
    usage: "Jednobojna štampa na svetloj površini.",
    surface: "light",
    previewClassName: "bg-background",
  }),
  asset({
    id: "wordmark-white",
    fileName: "resimo-probleme-wordmark-white",
    kind: "wordmark",
    label: "Beli naziv",
    usage: "Tamne površine kada znak nije potreban.",
    surface: "dark",
    previewClassName: "bg-brand-forest",
  }),
  asset({
    id: "mark-teal",
    fileName: "resimo-probleme-mark-teal",
    kind: "mark",
    label: "Tirkizni znak",
    usage: "Podrazumevani znak na svetlim površinama.",
    surface: "light",
    previewClassName: "bg-background",
  }),
  asset({
    id: "mark-forest",
    fileName: "resimo-probleme-mark-forest",
    kind: "mark",
    label: "Šumsko zeleni znak",
    usage: "Uredničke i jednobojne primene.",
    surface: "light",
    previewClassName: "bg-background",
  }),
  asset({
    id: "mark-white",
    fileName: "resimo-probleme-mark-white",
    kind: "mark",
    label: "Beli znak",
    usage: "Šumsko zelene i druge tamne površine.",
    surface: "dark",
    previewClassName: "bg-brand-forest",
  }),
  asset({
    id: "avatar",
    fileName: "resimo-probleme-avatar",
    kind: "avatar",
    label: "Avatar",
    usage: "Profilne slike i mali kružni formati.",
    surface: "any",
    previewClassName: "bg-background",
  }),
] as const satisfies readonly BrandAsset[];

type BrandAssetEntry = (typeof BRAND_ASSETS)[number];

export const brandAssetById = <Id extends BrandAssetEntry["id"]>(id: Id) =>
  BRAND_ASSETS.find((entry) => entry.id === id)! as Extract<
    BrandAssetEntry,
    { id: Id }
  >;

export const BRAND_PRIMARY_MARK = {
  png: "/brand/resimo-probleme-mark-teal-transparent.png",
} as const;
export const BRAND_ORGANIZATION_LOGO = {
  png: "/brand/resimo-probleme-avatar-painted.png",
} as const;

export const BRAND_SOCIAL_GUIDANCE_UPDATED_AT = "2026-09-21";

export type BrandSocialAssetKind = "account" | "profile" | "template";

export type BrandSocialAsset = {
  id: string;
  kind: BrandSocialAssetKind;
  fileName: string;
  label: string;
  platform: string;
  usage: string;
  width: number;
  height: number;
  safeArea: string;
  svg?: string;
  png: string;
  jpeg: string;
};

const socialAssetPath = (name: string, extension: "svg" | "png" | "jpg") =>
  `/brand/social/${name}.${extension}`;

type BrandSocialAssetDefinition = Omit<
  BrandSocialAsset,
  "svg" | "png" | "jpeg"
>;

const socialAsset = <const Entry extends BrandSocialAssetDefinition>(
  input: Entry,
) => ({
  ...input,
  svg: socialAssetPath(input.fileName, "svg"),
  png: socialAssetPath(input.fileName, "png"),
  jpeg: socialAssetPath(input.fileName, "jpg"),
});

export type BrandSocialSeries = {
  key: "svojim-recima" | "pitanje" | "dokaz" | "stvarni-trenutak";
  name: string;
  purpose: string;
  planningRole: string;
  assetIds: string[];
};

export const BRAND_SOCIAL_SERIES = [
  {
    key: "svojim-recima",
    name: "Svojim rečima",
    purpose: "Stvarni ljudi: terapeut govori svojim glasom, bez skripte.",
    planningRole: "Ljudi",
    assetIds: ["quote-square", "therapist-video-square", "vertical-video"],
  },
  {
    key: "pitanje",
    name: "Pitanje koje čujemo",
    purpose: "Razumemo gde si: stvarno pitanje, koristan odgovor.",
    planningRole: "Razumevanje",
    assetIds: [
      "question-square",
      "carousel-cover",
      "carousel-body",
      "carousel-end",
    ],
  },
  {
    key: "dokaz",
    name: "Pre nego što zakažeš",
    purpose:
      "Stvarna usluga: cena, termin, recenzija, proverljivo, ne obećano.",
    planningRole: "Usluga",
    assetIds: ["product-proof-square", "review-square"],
  },
  {
    key: "stvarni-trenutak",
    name: "Stvarni trenutak",
    purpose: "Za koga postojimo: kadar iz stvarnog života, posebno dijaspora.",
    planningRole: "Situacija",
    assetIds: ["lived-moment-square"],
  },
] as const satisfies readonly BrandSocialSeries[];

export const BRAND_SOCIAL_ASSETS = [
  {
    id: "profile-avatar",
    kind: "profile",
    fileName: "resimo-probleme-avatar",
    label: "Profilna slika",
    platform: "Sve mreže",
    usage: "Jedan prepoznatljiv avatar za sve zvanične naloge.",
    width: 1024,
    height: 1024,
    safeArea: "Znak ostaje čitljiv kada ga platforma iseče u krug.",
    svg: undefined,
    png: BRAND_ORGANIZATION_LOGO.png,
    jpeg: "/brand/resimo-probleme-avatar-painted.jpg",
  },
  socialAsset({
    id: "instagram-highlight-first-step",
    kind: "profile",
    fileName: "resimo-probleme-instagram-highlight-first-step-illustrated",
    label: "Highlight, prvi korak",
    platform: "Instagram",
    usage: "Naslovna slika za priče o prvom razgovoru i početku terapije.",
    width: 1080,
    height: 1920,
    safeArea:
      "Glavni motiv ostaje u centralnom krugu. Svi formati imaju punu papirnu pozadinu, bez providnosti.",
  }),
  socialAsset({
    id: "instagram-highlight-therapists",
    kind: "profile",
    fileName: "resimo-probleme-instagram-highlight-therapists-illustrated",
    label: "Highlight, terapeuti",
    platform: "Instagram",
    usage:
      "Ilustracija razgovora za priče o terapeutima. Nije portret stvarnog terapeuta.",
    width: 1080,
    height: 1920,
    safeArea:
      "Glavni motiv ostaje u centralnom krugu. Svi formati imaju punu papirnu pozadinu, bez providnosti.",
  }),
  socialAsset({
    id: "instagram-highlight-how-it-works",
    kind: "profile",
    fileName: "resimo-probleme-instagram-highlight-how-it-works-illustrated",
    label: "Highlight, kako radi",
    platform: "Instagram",
    usage: "Naslovna slika za objašnjenje izbora, zakazivanja i razgovora.",
    width: 1080,
    height: 1920,
    safeArea:
      "Glavni motiv ostaje u centralnom krugu. Svi formati imaju punu papirnu pozadinu, bez providnosti.",
  }),
  socialAsset({
    id: "instagram-highlight-questions",
    kind: "profile",
    fileName: "resimo-probleme-instagram-highlight-questions-conversation",
    label: "Highlight, pitanja",
    platform: "Instagram",
    usage: "Naslovna slika za česta pitanja i kratke odgovore.",
    width: 1080,
    height: 1920,
    safeArea:
      "Glavni motiv ostaje u centralnom krugu. Svi formati imaju punu papirnu pozadinu, bez providnosti.",
  }),
  socialAsset({
    id: "instagram-highlight-experiences",
    kind: "profile",
    fileName: "resimo-probleme-instagram-highlight-experiences-illustrated",
    label: "Highlight, iskustva",
    platform: "Instagram",
    usage:
      "Ilustrovana naslovna slika za priče o iskustvima. Sama ilustracija ne predstavlja stvarnog klijenta ni njegovu recenziju.",
    width: 1080,
    height: 1920,
    safeArea:
      "Glavni motiv ostaje u centralnom krugu. Svi formati imaju punu papirnu pozadinu, bez providnosti.",
  }),
  socialAsset({
    id: "facebook-cover",
    kind: "account",
    fileName: "resimo-probleme-facebook-cover",
    label: "Facebook naslovna slika",
    platform: "Facebook",
    usage: "Naslovna slika zvanične Facebook stranice.",
    width: 851,
    height: 315,
    safeArea: "Glavna poruka je udaljena od profilne slike i ivica kadra.",
  }),
  socialAsset({
    id: "linkedin-cover",
    kind: "account",
    fileName: "resimo-probleme-linkedin-cover",
    label: "LinkedIn naslovna slika",
    platform: "LinkedIn",
    usage: "Naslovna slika zvanične LinkedIn stranice.",
    width: 4200,
    height: 700,
    safeArea: "Ključni elementi ostaju u sredini zbog promenljivog kadriranja.",
  }),
  socialAsset({
    id: "youtube-banner",
    kind: "account",
    fileName: "resimo-probleme-youtube-banner",
    label: "YouTube baner",
    platform: "YouTube",
    usage: "Baner kanala prilagođen televizoru, računaru i telefonu.",
    width: 2560,
    height: 1440,
    safeArea:
      "Logotip i poruka su unutar centralne bezbedne zone 1544 × 423 px.",
  }),
  socialAsset({
    id: "question-square",
    kind: "template",
    fileName: "resimo-probleme-template-question-square",
    label: "Pitanje i odgovor",
    platform: "Instagram · Facebook · LinkedIn",
    usage: "Jedno stvarno pitanje i kratak, koristan odgovor.",
    width: 1080,
    height: 1080,
    safeArea: "Tekst ostaje najmanje 96 px od svake ivice.",
  }),
  socialAsset({
    id: "therapist-video-square",
    kind: "template",
    fileName: "resimo-probleme-template-therapist-video-square",
    label: "Terapeut u kadru",
    platform: "Instagram · Facebook",
    usage: "Naslovna slika videa sa stvarnim terapeutom i kratkom porukom.",
    width: 1080,
    height: 1080,
    safeArea: "Lice ostaje čisto, a naslov kratak i udaljen od ivica.",
  }),
  socialAsset({
    id: "lived-moment-square",
    kind: "template",
    fileName: "resimo-probleme-template-lived-moment-square",
    label: "Stvarni trenutak",
    platform: "Instagram · Facebook",
    usage: "Fotografija ili kadar koji nosi jednu kratku ljudsku poruku.",
    width: 1080,
    height: 1080,
    safeArea: "Poruka ne prekriva lice ili glavni detalj fotografije.",
  }),
  socialAsset({
    id: "product-proof-square",
    kind: "template",
    fileName: "resimo-probleme-template-product-proof-square",
    label: "Dokaz iz proizvoda",
    platform: "Instagram · Facebook · LinkedIn",
    usage:
      "Jedna proverljiva činjenica o profilu, ceni, terminu ili recenziji.",
    width: 1080,
    height: 1080,
    safeArea: "Prikaži samo stvarne podatke i ukloni lične informacije.",
  }),
  socialAsset({
    id: "carousel-cover",
    kind: "template",
    fileName: "resimo-probleme-template-carousel-cover",
    label: "Karusel, naslovna",
    platform: "Instagram · Facebook · LinkedIn",
    usage: "Prvi kadar karusela sa jednom jasnom temom.",
    width: 1080,
    height: 1350,
    safeArea: "Naslov i redni broj ostaju u centralnom 4:5 kadru.",
  }),
  socialAsset({
    id: "carousel-body",
    kind: "template",
    fileName: "resimo-probleme-template-carousel-body",
    label: "Karusel, sadržaj",
    platform: "Instagram · Facebook · LinkedIn",
    usage: "Jedna konkretna činjenica ili korak po kadru.",
    width: 1080,
    height: 1350,
    safeArea: "Jedna ideja po kadru, uz najmanje 96 px unutrašnje margine.",
  }),
  socialAsset({
    id: "carousel-end",
    kind: "template",
    fileName: "resimo-probleme-template-carousel-end",
    label: "Karusel, završni kadar",
    platform: "Instagram · Facebook · LinkedIn",
    usage: "Završni kadar sa jednim sledećim korakom.",
    width: 1080,
    height: 1350,
    safeArea: "Jedna radnja bez dodatnih konkurentskih poziva.",
  }),
  socialAsset({
    id: "vertical-video",
    kind: "template",
    fileName: "resimo-probleme-template-vertical-video",
    label: "Story i vertikalni video",
    platform: "Instagram · TikTok · YouTube",
    usage: "Naslovni kadar za Story, Reel, TikTok ili Short.",
    width: 1080,
    height: 1920,
    safeArea: "Važan sadržaj izbegava gornjih 14% i donjih 20% kadra.",
  }),
  socialAsset({
    id: "link-share",
    kind: "template",
    fileName: "resimo-probleme-template-link-share",
    label: "Deljenje linka",
    platform: "Facebook · LinkedIn · Open Graph",
    usage: "Naslovna slika za članak, vodič ili javnu stranicu.",
    width: 1200,
    height: 627,
    safeArea: "Naslov ostaje kratak i udaljen od automatskog kadriranja.",
  }),
  socialAsset({
    id: "youtube-thumbnail",
    kind: "template",
    fileName: "resimo-probleme-template-youtube-thumbnail",
    label: "YouTube naslovna slika videa",
    platform: "YouTube",
    usage:
      "Naslov videa uz prostor za stvarnog terapeuta ili kadar iz razgovora.",
    width: 3840,
    height: 2160,
    safeArea: "Tekst ne ulazi u donji desni ugao sa trajanjem videa.",
  }),
  socialAsset({
    id: "quote-square",
    kind: "template",
    fileName: "resimo-probleme-template-quote-square",
    label: "Svojim rečima",
    platform: "Instagram · Facebook · LinkedIn",
    usage: "Jedna stvarna rečenica terapeuta, uz ime, zvanje i portret.",
    width: 1080,
    height: 1080,
    safeArea:
      "Citat je jedna misao. Bez parafraziranja i marketinškog doterivanja.",
  }),
  socialAsset({
    id: "review-square",
    kind: "template",
    fileName: "resimo-probleme-template-review-square",
    label: "Pre nego što zakažeš",
    platform: "Instagram · Facebook · LinkedIn",
    usage:
      "Jedna stvarna recenzija sa platforme, anonimizovana i uz saglasnost.",
    width: 1080,
    height: 1080,
    safeArea:
      "Bez imena klijenta i bez detalja koji mogu da otkriju identitet.",
  }),
] as const satisfies readonly BrandSocialAsset[];

export type BrandRuntimeAsset = {
  id: string;
  label: string;
  path: string;
  mimeType: "image/png" | "image/svg+xml" | "image/x-icon";
  sizes: string;
  purpose?: "any" | "maskable" | "monochrome";
};

export const BRAND_RUNTIME_ASSETS = [
  {
    id: "favicon",
    label: "Favicon",
    path: "/favicon.ico",
    mimeType: "image/x-icon",
    sizes: "16x16 32x32 48x48",
  },
  {
    id: "app-192",
    label: "Aplikacijska ikona 192",
    path: assetPath("resimo-probleme-app-192", "png"),
    mimeType: "image/png",
    sizes: "192x192",
    purpose: "any",
  },
  {
    id: "app-512",
    label: "Aplikacijska ikona 512",
    path: assetPath("resimo-probleme-app-512", "png"),
    mimeType: "image/png",
    sizes: "512x512",
    purpose: "any",
  },
  {
    id: "app-maskable-512",
    label: "Maskabilna aplikacijska ikona",
    path: assetPath("resimo-probleme-app-maskable-512", "png"),
    mimeType: "image/png",
    sizes: "512x512",
    purpose: "maskable",
  },
  {
    id: "app-monochrome",
    label: "Jednobojna aplikacijska ikona",
    path: assetPath("resimo-probleme-app-monochrome", "png"),
    mimeType: "image/png",
    sizes: "512x512",
    purpose: "monochrome",
  },
  {
    id: "apple-touch-icon",
    label: "Apple touch ikona",
    path: assetPath("resimo-probleme-apple-touch-icon", "png"),
    mimeType: "image/png",
    sizes: "180x180",
  },
] as const satisfies readonly BrandRuntimeAsset[];

type BrandRuntimeAssetEntry = (typeof BRAND_RUNTIME_ASSETS)[number];

export const brandRuntimeAssetById = <Id extends BrandRuntimeAssetEntry["id"]>(
  id: Id,
) =>
  BRAND_RUNTIME_ASSETS.find((entry) => entry.id === id)! as Extract<
    BrandRuntimeAssetEntry,
    { id: Id }
  >;
